import { action, computed, observable } from 'mobx'
import type {
	Localization,
	OrderTypeFlow,
	LocalizationDependencies,
	Configuration,
	SavedAddress,
	BaseSavedAddress,
	NonSavedAddress,
	StoreResponse,
	Store,
	OtherSavedAddress
} from './types'
import { AddressLabel, LocalizationFlow, FlowStep, OrderType } from './types'
import { EVENTS } from './constants'

const MAXIMUM_SAVED_ADDRESSES = 10
const ORDER_TYPE_MAPPING = {
	delivery: 'Delivery',
	pickup: 'Takeaway',
	peakup: 'Takeaway',
	'dine-in': 'Dine In',
}

export default class LocalizationStore implements Localization {
	constructor(dependencies: LocalizationDependencies) {
		this.dependencies = dependencies
	}

	private readonly dependencies: LocalizationDependencies

	config = () =>
		// must be replaced by the content of appParams.features.localization
		this.dependencies.config() || defaultLocalization

	get = () => this.dependencies.get(this.config().storageType)

	@computed get savedAddressesEnabled(): boolean {
		return !!this.config().savedAddressesEnabled
	}

	@observable actualFlow: LocalizationFlow | null = this.get()

	@observable processingFlow: LocalizationFlow | null = null

	@observable editing = false

	@observable orderTypeFlows: OrderTypeFlow[] = []

	@observable storesOnActualAddress: StoreResponse[] = []

	@action setOrderTypeFlows = (orderTypeFlows: OrderTypeFlow[]) => {
		this.orderTypeFlows = orderTypeFlows
	}

	@observable savedAddresses: SavedAddress[] = []

	@observable _savedAddressesFetched = false

	@computed get savedAddressesFetched(): boolean {
		return this._savedAddressesFetched
	}

	@action fetchSavedAddresses = async () => {
		this.savedAddresses = await this.dependencies.fetchSavedAddresses()
		this._savedAddressesFetched = true
	}

	@action clearSavedAddresses = () => {
		this.savedAddresses = []
		this._savedAddressesFetched = false
	}

	@action addSavedAddress = async (flow: LocalizationFlow) => {
		try {
			const savedAddress = await this.dependencies.createSavedAddress(flow)
			this.savedAddresses.push(savedAddress)
			this.dependencies.emitAddressSavedEvent(savedAddress)

			return savedAddress
		} catch (err) {
			this.dependencies.emitAddressFailedToSaveEvent(err)
			throw err
		}
	}

	@action editSavedAddress = async (flow: LocalizationFlow) => {

		if(!flow.address || !flow.savedAddressId || !flow.savingAddressData) {
			return
		}

		await this.dependencies.updateSavedAddress(flow)

		const savedAddress: BaseSavedAddress = {
			...flow.address,
			_id: flow.savedAddressId!,
			label: flow.savingAddressData.label,
			locationDetails: flow.locationDetails || undefined,
		}

		if(flow.savingAddressData?.customLabel) {
			(savedAddress as OtherSavedAddress).customLabel = flow.savingAddressData?.customLabel
		}

		const index = this.savedAddresses.findIndex((sa) => sa._id === savedAddress._id)
		this.savedAddresses[index] = savedAddress as SavedAddress
	}

	@action removeSavedAddress = async (addressId: string) => {
		await this.dependencies.deleteSavedAddress(addressId)
		this.savedAddresses = [...this.savedAddresses.filter((sa) => sa._id !== addressId)]
	}

	getSavedAddress = (addressId: string) => this.savedAddresses.find((address) => address._id === addressId)

	@computed get reachedMaximumSavedAddresses(): boolean {
		return this.savedAddresses.length >= MAXIMUM_SAVED_ADDRESSES
	}

	@observable started = false

	@observable currentStep: FlowStep | null = null

	@computed get alreadyLocalized(): boolean {
		return (
			this.actualFlow?.address !== null &&
			this.actualFlow?.address !== undefined &&
			this.actualFlow?.store !== null &&
			this.actualFlow?.store !== undefined
		)
	}

	@observable startedFromSavedAddress = false

	editStartingStep: FlowStep | null = null

	/*
	 * Fetches and filters out, and saves relevant stores for the given address
	 */
	matchAndSetStoreForAddress = async (address: NonSavedAddress) => {
		if (!this.processingFlow?.orderTypeFlow) {
			return
		}

		const { stores, shouldGoToFirstStoreFound } = await this.dependencies.getStoresOnAddress(address)
		const { orderType } = this.processingFlow.orderTypeFlow

		const relevantStores = stores.filter(
			({ availability }) => availability?.[orderType]?.availableForAddress && availability?.[orderType]?.availableNow && (availability.orderOnline || availability.preOrder)
		)

		// We set the store automaticaly only if
		// - one store is found,
		// - or many stores found, but the auto store seletion is enabled
		if (relevantStores.length === 1 || (relevantStores.length > 0 && shouldGoToFirstStoreFound)) {
			const [firstStore] = relevantStores
			let storeToSet = null

			if(this.processingFlow.store) {
				storeToSet = relevantStores.find(s => s.id === this.processingFlow?.store?.id)
			}

			if(!storeToSet) {
				storeToSet = firstStore
			}

			this.processingFlow.setStore({
				id: storeToSet.id,
				name: storeToSet.title,
				address: storeToSet.address.formatted,
			})
		}

		// Else we reset the store
		else {
			this.processingFlow.resetStore()
		}

		this.dependencies.updateMapStores(stores)
		this.processingFlow.setOrderTypeFlowDisplayAvailableStores(true)
	}

	availableStoresOnActualAddress = async () => {
		if (this.actualFlow?.address) {
			const orderType = this.actualFlow?.orderTypeFlow?.orderType
			const orderTypeRequireAddress = this.actualFlow?.orderTypeFlow?.requireAddress
			const { stores } = await this.dependencies.getStoresOnAddress(this.actualFlow.address)

			if (orderType && stores.length > 0) {
				this.storesOnActualAddress = stores.filter(
					({ availability }) => (!orderTypeRequireAddress || availability?.[orderType]?.availableForAddress) && (availability.orderOnline || availability.preOrder)
				)

				return
			}
		}

		this.storesOnActualAddress = []
	}

	@action start = async (preSelectedStore: Store | null = null) => {
		// stop the flow if order type flows are unavailable
		const { orderTypeFlowsUnavailable, skipOrderTypeSelection } = await this.getAndSetOrderTypeFlows()
		if (orderTypeFlowsUnavailable) {
			this.dependencies.emitOrderTypeFlowsUnavailable()
			return
		}

		// Start and instanciate the flow
		this.started = true
		this.processingFlow = new LocalizationFlow()

		if(preSelectedStore) {
			this.processingFlow.setStore(preSelectedStore)
		}

		// if config skipOrderTypeSelection is true and only one order type flow is available
		// we force the order type selection and move to the next step
		if (skipOrderTypeSelection) {
			this.processingFlow.setOrderTypeFlow(this.orderTypeFlows[0])
			this.nextStep()
			this.sendLocalizationStartEvent()
			return
		}

		this.currentStep = FlowStep.OrderTypeSelection
	}

	@action startFromSavedAddress = async (address?: SavedAddress, editingAddress = false) => {
		this.startedFromSavedAddress = true
		this.sendLocalizationStartEvent()

		const filteredOrderTypesFlows = await this.getFilteredOrderTypeFlows()
		const deliveryOrderTypeFlow = filteredOrderTypesFlows.find((fotf) => fotf.orderType === OrderType.Delivery)

		// Delivery is available, continuing the flow
		if (deliveryOrderTypeFlow) {
			this.processingFlow = new LocalizationFlow()
			this.processingFlow.setOrderTypeFlow(deliveryOrderTypeFlow)
			this.goToStep(FlowStep.AddressSelection)

			// Address selection
			if (address && editingAddress === false) {
				this.processingFlow.selectSavedAddress(address)
				await this.matchAndSetStoreForAddress(address)
				this.started = true
				this.nextStep()
			}

			// Address edition
			if(address && editingAddress === true) {
				this.started = true
				this.processingFlow.selectSavedAddress(address)
				this.processingFlow.setSavingAddressLabel(address.label)
				if (address.label === AddressLabel.Other) {
					this.processingFlow.setSavingAddressCustomLabel(address.customLabel)
				}
			}

			// Address creation
			if(!address) {
				this.started = true
				this.processingFlow.setSavingAddressLabel(AddressLabel.Home)
			}

			return {}
		}
		return { deliveryUnavailable: true }
	}

	sendLocalizationStartEvent = () => {
		this.sendLocalizationEvent(EVENTS.LOCALIZATION_START)
	}

	sendLocalizationEvent = (eventName: string, params?: any) => {
		this.dependencies.sendLocalizationEvent(eventName, params)
	}

	formateLocalizationEventOrderType = (orderType: OrderType) => ORDER_TYPE_MAPPING[orderType] || null

	shouldSkipOrderTypeSelection = () => this.config().canSkipOrderTypeSelection && this.orderTypeFlows.length === 1

	getAndSetOrderTypeFlows = async() => {
		const filteredOrderTypeFlows = await this.getFilteredOrderTypeFlows()
		this.setOrderTypeFlows(filteredOrderTypeFlows)

		return {
			orderTypeFlowsUnavailable: this.orderTypeFlows.length === 0,
			skipOrderTypeSelection: this.shouldSkipOrderTypeSelection()
		}

		// orderTypeFlowsUnavailable: indicate that all the order type flows are unavailable, it happens when
		// 	- all stores are closed
		// 	- defined order type flows don't match with the available stores

		// skipOrderTypeSelection: indicate that order type selection must be skipped, it happens when
		// 	- localization configuration has canSkipOrderTypeSelection set to true
		// 	- only one order type flow is available
	}

	getFilteredOrderTypeFlows = async () => {
		const stores = await this.dependencies.getStores()
		const deliveryEnabled = stores.deliveryStores === true || false
		const pickupEnabled = stores.disablePickup === false || false

		// Filter the order type flows
		return this.config().orderTypeFlows.filter((dotf) => {
			// if deliveryEnabled : requireAddress has to be true
			if (dotf.orderType === OrderType.Delivery && deliveryEnabled && dotf.requireAddress) {
				return true
			}

			// if pickEnabled : requireAddress has to be false
			if (dotf.orderType === OrderType.Pickup && pickupEnabled && !dotf.requireAddress) {
				return true
			}

			return false
		})
	}

	@action abort = () => {
		this.started = false
		this.startedFromSavedAddress = false
		this.processingFlow = null
		this.editing = false
		this.currentStep = null
		this.dependencies.clearCouponToApply()
		this.editStartingStep = null
	}

	@action end = async () => {
		this.started = false
		this.startedFromSavedAddress = false
		this.currentStep = null

		this.editStartingStep = null


		if (!this.processingFlow?.store || !this.processingFlow.address) {
			this.processingFlow = null
			return
		}

		const response = await this.dependencies.initSessionAndLoadMenu(this.processingFlow.store.id)

		if (response === true && this.processingFlow.savingAddressData) {
			// Editing saved address
			if(this.processingFlow.savedAddressId) {
				try {
					await this.editSavedAddress(this.processingFlow)
				} catch (_err) {
					console.error('Editing saved address error : ', _err)
				}
			}
			// Adding saved address
			else {
				try {
					const savedAddress = await this.addSavedAddress(this.processingFlow)
					this.processingFlow.selectSavedAddress(savedAddress)
				} catch (_err) {
					console.error('Adding saved address error : ', _err)
				}
			}
		}

		// Initiate actual flow & redirect to localized menu
		if (response === true) {
			const flow = new LocalizationFlow()
			flow.setOrderTypeFlow(this.processingFlow.orderTypeFlow!)
			flow.setStore(this.processingFlow.store)
			flow.setSavedAddressId(this.processingFlow.savedAddressId)
			flow.setAddress(this.processingFlow.address)
			flow.setLocationDetails(this.processingFlow.locationDetails)

			this.actualFlow = flow
			this.dependencies.save(flow!, this.config().storageType)

			await this.dependencies.goToLocalizedMenu()
		}

		// If processing flow store is not available and an actual flow store is already set (ex, durimg editing),
		// we re-init the session of the previous store
		else if (this.actualFlow?.store?.id) {
			await this.dependencies.initSession(this.actualFlow.store.id)
		}

		if (this.actualFlow?.store && this.actualFlow.orderTypeFlow) {
			this.sendLocalizationEvent(EVENTS.LOCALIZATION_FINISH, {
				postLocalisation: 'true', // a string, not a boolean!
				storeID: this.actualFlow.store.id,
				disposition: this.actualFlow.orderTypeFlow?.orderType || 'unknown'
			})
		}

		this.processingFlow = null
	}

	@action edit = async() => {
		if (!this.actualFlow) {
			console.error('error edit: actualFlow is null')
			return
		}

		// stop the flow if order type flows are unavailable
		const { orderTypeFlowsUnavailable } = await this.getAndSetOrderTypeFlows()
		if(orderTypeFlowsUnavailable) {
			this.dependencies.emitOrderTypeFlowsUnavailable()
			return
		}

		this.started = true
		this.editing = true
		this.currentStep = null

		this.processingFlow = new LocalizationFlow()
		this.processingFlow.setOrderTypeFlow(this.actualFlow.orderTypeFlow!)
		this.processingFlow.setStore(this.actualFlow.store!)
		this.processingFlow.setAddress(this.actualFlow.address!)
		this.processingFlow.setSavedAddressId(this.actualFlow.savedAddressId)
		this.processingFlow.setLocationDetails(this.actualFlow.locationDetails)
		this.processingFlow.setOrderTypeFlowDisplayAvailableStores(true)
	}

	@action editAction = (flowStep: FlowStep) => {
		this.sendLocalizationStartEvent()
		this.goToStep(flowStep)
		this.editStartingStep = flowStep
	}

	@action canGoToStep = (step: FlowStep): boolean => {
		if (this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
			return false
		}

		return this.processingFlow.orderTypeFlow.steps.some((s) => s === step)
	}

	@action goToStep = (step: FlowStep) => {
		if (this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
			return
		}

		const stepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s) => s === step)
		this.currentStep = this.processingFlow.orderTypeFlow.steps[stepIndex]
	}

	@action nextStep = () => {
		if (!this.processingFlow?.orderTypeFlow) {
			return
		}

		// if store and address exist, ending the flow here
		// check additonnaly if LocationDetailsSelection optional step exist in the flow,
		// and if yes, the current step should be LocationDetailsSelection
		if(this.processingFlow.store && this.processingFlow.address &&
			(!this.processingFlow.orderTypeFlow.steps.includes(FlowStep.LocationDetailsSelection)
			|| this.currentStep === FlowStep.LocationDetailsSelection)) {
			this.end()
			return
		}

		// if we are at the start of the localization flow (order type view), move to the first step
		if (this.currentStep === FlowStep.OrderTypeSelection) {
			this.currentStep = this.processingFlow.orderTypeFlow.steps[0]
			return
		}

		const modalCurrentStepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s) => s === this.currentStep)

		// if current step is the last step
		if (modalCurrentStepIndex === this.processingFlow.orderTypeFlow.steps.length - 1) {
			this.end()
			return
		}

		// move to the next step
		this.currentStep = this.processingFlow.orderTypeFlow.steps[modalCurrentStepIndex + 1]
	}

	@action previousStep = async () => {
		if (this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
			return
		}

		const modalCurrentStep = this.processingFlow.orderTypeFlow.steps.find((s) => s === this.currentStep)
		const modalCurrentStepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s) => s === this.currentStep)

		// while editing, previous step action go back the user to the edit modal view if we reached back the edit starting step
		if(this.editing && modalCurrentStep === this.editStartingStep) {
			this.currentStep = null
			return
		}

		// if current step is the first step,
		if (modalCurrentStepIndex === 0) {

			// If flow started from saved address, OR skipOrderTypeSelection is true, we abort
			const { skipOrderTypeSelection } = await this.getAndSetOrderTypeFlows()
			if (this.startedFromSavedAddress || skipOrderTypeSelection) {
				this.abort()
			}

			// Else return to the start of the localization flow (order type view)
			else {
				this.currentStep = FlowStep.OrderTypeSelection
			}

			return
		}

		// move to the previous step
		this.currentStep = this.processingFlow.orderTypeFlow.steps[modalCurrentStepIndex - 1]
	}

	@action goToLocalizedMenu = async () => {
		// init a new session before redirecting, in case of an order-lock is active
		const result = await this.dependencies.initSession(this.actualFlow?.store?.id)
		if (result === '') {
			return
		}

		// handle the menu redirection here...
		await this.dependencies.goToLocalizedMenu()
	}

	@action getLocalizedMenuPath = () => this.dependencies.getLocalizedMenuPath()

	@action getValidateAddress = () => {
		if (this.actualFlow?.address) {
			return this.dependencies.getValidateAddress(this.actualFlow.address)
		}
	}

	@action forceLocalizationToStore = async (storeId: string) => {
		const store = await this.dependencies.getStoreDetailsById(storeId)
		this.dependencies.checkPickupAvailabilityByStore(store)

		const filteredOrderTypeFlows = await this.getFilteredOrderTypeFlows()
		const pickupOrderTypeFlow = filteredOrderTypeFlows.find((otf) => otf.orderType === OrderType.Pickup)

		// If pickup exists in the available order type flow
		if (pickupOrderTypeFlow) {
			const flow = new LocalizationFlow()
			flow.setOrderTypeFlow(pickupOrderTypeFlow)
			flow.setAddress({
				formatted: store.location.address,
				location: {
					lat: store.location.coordinates.lat,
					lng: store.location.coordinates.lng,
				},
				components: [],
			})
			flow.setStore({
				id: store.id,
				name: store.title,
				address: store.location.address,
			})

			this.actualFlow = flow

			// handle the menu redirection here...
			if (this.actualFlow?.store) {
				await this.dependencies.initSessionAndLoadMenu(this.actualFlow.store.id)
				await this.dependencies.goToLocalizedMenu()
			}

			this.dependencies.save(this.actualFlow!, this.config().storageType)
		}
	}

	async getAddressFromText(addressText: string) : Promise<NonSavedAddress> {
		const storeGeoCodeResult = await this.dependencies.getGeoCodeFromAddressText(addressText)
		return {
			formatted: storeGeoCodeResult.formatted_address,
			location: {
				lat: storeGeoCodeResult.geometry.location.lat(),
				lng: storeGeoCodeResult.geometry.location.lng()
			},
			components: storeGeoCodeResult.address_components
		}
	}
}

const defaultOrderTypeFlows: OrderTypeFlow[] = [
	{
		orderType: OrderType.Delivery,
		orderTypeImageSrc: '',
		orderTypeTranslationKey: 'delivery',
		steps: [FlowStep.AddressSelection, FlowStep.LocationDetailsSelection, FlowStep.StoreOnMapSelection],
		requireAddress: true,
	},
	{
		orderType: OrderType.Pickup,
		orderTypeImageSrc: '',
		orderTypeTranslationKey: 'peakup',
		steps: [FlowStep.StoreOnMapSelection],
		requireAddress: false,
	},
]

const defaultLocalization: Configuration = {
	orderTypeFlows: defaultOrderTypeFlows,
	canSkipOrderTypeSelection: true,
	displayToMenuButton: true,
	enabled: true,
	storageType: 'local'
}
